import { Box, Stack, Typography } from '@mui/material'
import { forwardRef, type FC, type ReactNode } from 'react'
import { Collapse } from 'react-collapse'
import {
  Primary,
  SectionBackgroundColor,
  SectionColor,
  White,
} from 'src/features/theme/KdsThemeColor'

const tableCellSx = {
  display: 'table-cell' as const,
  backgroundColor: SectionBackgroundColor,
}

export const SectionWrapper = forwardRef<
  HTMLDivElement,
  { children?: ReactNode }
>(({ children }, ref) => (
  <Stack ref={ref} direction="row" alignItems="center" sx={{ height: 46 }}>
    {children}
  </Stack>
))
SectionWrapper.displayName = 'SectionWrapper'

export const SectionOpenButtonWrapper: FC<{ children?: ReactNode }> = ({
  children,
}) => <Box sx={{ ml: 2 }}>{children}</Box>

export const SectionNameParagraphWrapper: FC<{
  children?: ReactNode
  'data-testid'?: string
}> = ({ children, ...props }) => (
  <Box sx={{ flex: 1, minWidth: 0 }} {...props}>
    {children}
  </Box>
)

export const SectionNameParagraph: FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <Box sx={{ width: '100%', pl: '10px', color: SectionColor }}>{children}</Box>
)

export const Table: FC<{ children?: ReactNode; onClick?: () => void }> = ({
  children,
  onClick,
}) => (
  <Box
    sx={{
      display: 'table',
      borderCollapse: 'collapse',
      height: 46,
      width: '100%',
      lineHeight: '46px',
    }}
    onClick={onClick}
  >
    {children}
  </Box>
)

export const SectionTableCell: FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <Box sx={{ ...tableCellSx, width: '100%', maxWidth: 0, overflow: 'hidden' }}>
    {children}
  </Box>
)

export const BasicTableCell: FC<{ children?: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      ...tableCellSx,
      border: '1px solid rgba(0, 0, 0, 0.12)',
      minWidth: '90px',
      color: White,
    }}
  >
    {children}
  </Box>
)

export const DraggingPlaceholder: FC = () => (
  <Box
    sx={{
      display: 'flex',
      height: 46,
      lineHeight: '46px',
      width: '100%',
      backgroundColor: Primary,
    }}
  />
)

export const DragHandle = forwardRef<HTMLDivElement, { children?: ReactNode }>(
  ({ children }, ref) => (
    <Box
      ref={ref}
      sx={{
        ml: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2rem',
        height: '100%',
        zIndex: 1,
        outline: 'none',
        cursor: 'grab',
      }}
    >
      {children}
    </Box>
  ),
)
DragHandle.displayName = 'DragHandle'

export const ColumnText: FC<{ children?: ReactNode }> = ({ children }) => (
  <Typography sx={{ ml: '10px', color: SectionColor }}>{children}</Typography>
)

export const CheckBoxText: FC<{ children?: ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="span" sx={{ color: SectionColor }}>
    {children}
  </Typography>
)

export const ConfirmModalContentList: FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <Box
    component="ul"
    sx={{ maxHeight: 220, pl: '20px', pb: '30px', overflowY: 'scroll' }}
  >
    {children}
  </Box>
)

export const SectionContentWrapper: FC<{
  isOpened: boolean
  children?: ReactNode
}> = ({ isOpened, children }) => (
  <Collapse isOpened={isOpened}>{children}</Collapse>
)

export const HandleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    focusable="false"
    aria-hidden="true"
  >
    <path fill="none" d="M0 0h24v24H0V0z"></path>
    <path
      fill={White}
      d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
    ></path>
  </svg>
)
